'use client';

// imports
import { useState, useEffect, useRef } from 'react';
import { Shield, Lock, ArrowRight, BrainCircuit, Goal, Binoculars, Blocks, Brain} from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTASection from '../home-page/components/CTASection';
import { useSearchParams } from 'next/navigation';
import { useInView } from '@/hooks/useInView';

import { Suspense } from 'react';

// types
interface TeamMember {
  id: number;
  name: string;
  suffix?: string;
  roles: string[];
  description: string;
  initials: string;
  color: string;
  gradient: string;
  image?: string;
  linkedin?: string;
  email?: string;
  github?: string;
}

// constants
const base ='transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]';
const hidden = 'opacity-0 translate-y-8';
const show = 'opacity-100 translate-y-0';

//data
const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: 'Mr. Dan Marlou Arevalo',
    roles: ['Data Scientist', 'Researcher'],
    description: 'Researcher focusing on AI-driven healthcare solutions and deep learning architectures for non-invasive diagnostics.',
    initials: 'DA',
    color: '#3B82F6',
    gradient: 'from-blue-500 to-blue-700',
    image: '/assets/images/dan.png',
    linkedin: 'https://www.linkedin.com/in/danmarlouarevalo/',
    email: 'mailto:danmarlouarevalo@gmail.com',
    github: 'https://github.com/DZero04',
  },
  {
    id: 2,
    name: 'Ms. Ellen Kaye Isip',
    roles: ['Data Scientist', 'Researcher'],
    description: 'Specializes in designing deep learning architectures for AI-driven, non-invasive diagnostic systems.',
    initials: 'EK',
    color: '#6366F1',
    gradient: 'from-indigo-500 to-violet-700',
    image: '/assets/images/ellen.png',
    linkedin: 'https://www.linkedin.com/in/ellen-kaye-isip-a8b375313/',
    email: 'mailto:ellenkayeisip@gmail.com',
    github: 'https://github.com/lnkayeeee',
  },
  {
    id: 3,
    name: 'Engr. Jamal Kay B. Rogers',
    suffix: 'PhD',
    roles: ['Research Adviser', 'Professor'],
    description: 'Provides expert oversight on model design, validation, and implementation of deep learning architectures for non-invasive diagnostic systems.',
    initials: 'JK',
    color: '#0EA5E9',
    gradient: 'from-sky-500 to-cyan-600',
    image: '/assets/images/sir_jams.png',
    linkedin: 'https://www.linkedin.com/in/jamal-kay-rogers-5459283ab/',
    email: 'mailto:jamalkay.rogers@usep.edu.ph',
    github: 'https://github.com/jamalrogers',
  },
];

const roleColors = [
  "bg-blue-50 text-blue-600 border-blue-200",
  "bg-indigo-50 text-indigo-600 border-indigo-200",
  "bg-cyan-50 text-cyan-600 border-cyan-200",
];

const ethicsItems = [
  {
    icon: Shield,
    title: 'Responsible AI',
    description: 'Our models are designed to be explainable—supporting informed decisions while respecting clinical expertise.',
    bg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
    border: 'border-blue-200',
    hoverBorder: 'hover:border-blue-200',
    badge: 'Transparency',
    badgeColor: 'bg-blue-500/10 text-blue-400 border-blue-400/20',
  },
  {
    icon: Lock,
    title: 'Data Privacy',
    description: 'Your data is protected—ensuring privacy, security, and full user control at every step.',
    bg: 'bg-violet-500/10',
    iconColor: 'text-violet-400',
    border: 'border-violet-200',
    hoverBorder: 'hover:border-violet-200',
    badge: 'Security',
    badgeColor: 'bg-violet-500/10 text-violet-400 border-violet-400/20',
  },
  {
    icon: Brain,
    title: 'AI as Support',
    description: 'Designed to assist—not replace—medical professionals, ensuring decisions remain guided by human expertise.',
    bg: 'bg-cyan-500/10',
    iconColor: 'text-sky-400',
    border: 'border-sky-200',
    hoverBorder: 'hover:border-sky-200',
    badge: 'Human Oversight',
    badgeColor: 'bg-cyan-500/10 text-cyan-300 border-cyan-400/20',
  },
];


function AboutPageContent() {

  // hooks
  const { ref: heroInViewRef, isVisible: heroVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  const { ref: missionRef, isVisible: missionVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  const { ref: ethicsRef, isVisible: ethicsVisible } = useInView<HTMLDivElement>({
    threshold: 0.15,
    once: true,
  });

  // state
  const [activeIndex, setActiveIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });
  const [activeSocial, setActiveSocial] = useState<'linkedin' | 'email' | 'github' | null >(null);
  const [highlightActive, setHighlightActive] = useState(false);
  const [triggerKey, setTriggerKey] = useState(0);

  // refs
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const heroRef = useRef<HTMLDivElement>(null);

  // params
  const searchParams = useSearchParams();
  
  // utils
  const getShortName = (fullName: string) => {
    const parts = fullName.split(' ');
    if (parts.length <= 2) return fullName;

    return `${parts[0]} ${parts[parts.length - 1]}`;
  };

  const getCardPosition = (index: number) => {
      if (index === activeIndex) return "center";
      if (index === (activeIndex - 1 + teamMembers.length) % teamMembers.length) return "left";
      if (index === (activeIndex + 1) % teamMembers.length) return "right";
      return "hidden";
  };

  // handlers
  const handleCardClick = (index: number) => {
    setActiveIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;

    const distance = touchStartX.current - touchEndX.current;

    const threshold = 50;
    if (distance > threshold) {
      //swipe left
      setActiveIndex((prev) => (prev + 1) % teamMembers.length);
    } else if (distance < -threshold) {
      //swipe right
      setActiveIndex((prev) => (prev - 1 + teamMembers.length) % teamMembers.length);
    }
    // reset
    touchStartX.current = null;
    touchEndX.current = null;
  };


  // effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: ((e.clientX - rect.left) / rect.width) * 100,
          y: ((e.clientY - rect.top) / rect.height) * 100,
        });
      }
    };
    const el = heroRef.current;
    el?.addEventListener('mousemove', handleMouseMove);
    return () => el?.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const social = searchParams.get('social') as
      | 'linkedin'
      | 'email'
      | 'github'
      | null;

    if (!social) return;

    // 1. Find correct member
    const index = teamMembers.findIndex(member => member[social]);
    if (index !== -1) {
      setActiveIndex(index);
    }

    // 2. Scroll AFTER render (important)
    setTimeout(() => {
      const teamSection = document.getElementById('team');
      if (teamSection) {
        teamSection.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 300);

    // 3. Trigger highlight
    setActiveSocial(social);
    setHighlightActive(true);

    const timer = setTimeout(() => {
      setHighlightActive(false);
    }, 1500);

    return () => clearTimeout(timer);

  }, [searchParams]);

  useEffect(() => {
    if (!activeSocial) return;

    const index = teamMembers.findIndex(member => member[activeSocial]);
    if (index !== -1) {
      setActiveIndex(index);
    }
  }, [activeSocial]);

  // helpers
  const setRefs = (el: HTMLDivElement | null) => {
    if (!el) return;

    // mouse tracking
    heroRef.current = el;

    // inView
    heroInViewRef.current = el;
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      
      <main className="flex-1" >
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

        {/*hero section*/}
        <section
          ref={setRefs}
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 50%, #eef2ff 100%)',
            minHeight: '95vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* glow effect */}
          <div
            className="absolute inset-0 pointer-events-none transition-all duration-700"
            style={{
              background: `radial-gradient(ellipse 55% 45% at ${mousePos.x}% ${mousePos.y}%, rgba(59,130,246,0.12) 0%, rgba(99,102,241,0.07) 45%, transparent 70%)`,
            }}
          />

          {/* deco blobs */}
          <div className="absolute top-0 left-0 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.10) 0%, transparent 70%)', transform: 'translate(-30%, -30%)' }} />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.09) 0%, transparent 70%)', transform: 'translate(30%, 30%)' }} />

          {/* dot grid */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.35]" style={{ backgroundImage: 'radial-gradient(circle, #bfdbfe 1px, transparent 1px)', backgroundSize: '36px 36px' }} />

          <div className="section-container w-full relative z-10 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-40 sm:gap-30 items-center">

              {/* left */}
              <div>

                <h1 className= {`${base} ${heroVisible ? show : hidden} font-extrabold leading-none mb-6 text-slate-900`}
                  style={{ fontSize: 'clamp(3rem, 7vw, 5.5rem)', letterSpacing: '-0.03em' }}>
                  About<br />
                  <span className='bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent'>Insighta</span>
                  <span className="text-slate-300"> AI</span>
                </h1>

                <p className={`${base} delay-100 ${heroVisible ? show : hidden} text-base leading-relaxed mb-10 max-w-xl md:text-lg  text-slate-600`}>
                  We’re building smarter, non-invasive AI tools to detect diabetes risk early—bringing powerful health insights closer to everyday people. 
                  By combining advanced deep learning with accessible technology, we aim to make early screening simple, fast, and available anytime, anywhere.
                </p>

                <div className={`${base} delay-200 ${heroVisible ? show : hidden} flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-4 w-full sm:w-auto`}>
                  {/* mission button */}
                    <button
                      onClick={() => {
                        if (missionRef.current) {
                          const yOffset = -80; 
                          const y =
                            missionRef.current.getBoundingClientRect().top +
                            window.scrollY +
                            yOffset;

                          window.scrollTo({
                            top: y,
                            behavior: 'smooth',
                          });
                        }
                      }}
                      className="w-full sm:w-auto justify-center inline-flex items-center gap-2 
                      bg-gradient-to-r from-blue-500 to-blue-600 
                      text-white font-semibold rounded-full 
                      px-7 py-3.5 text-base 
                      shadow-lg shadow-blue-500/25
                      hover:shadow-blue-500/40
                      hover:from-blue-600 hover:to-blue-700
                      hover:scale-[1.02]
                      transition-all duration-200 active:scale-95
                      group"
                    >
                      <Goal size={18} />
                      Mission
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                    </button>

                    {/* ethics button*/}
                    <a
                      href="#ethics"
                      className="w-full sm:w-auto justify-center inline-flex items-center gap-2 
                      bg-white text-blue-600 font-semibold rounded-full 
                      px-7 py-3.5 text-base 
                      border border-blue-200
                      shadow-lg shadow-blue-500/10
                      hover:bg-blue-50 
                      hover:shadow-blue-500/20
                      hover:scale-[1.02]
                      transition-all duration-200 active:scale-95
                      group"
                    >
                      {/* icon */}
                      <Shield size={16} />

                      Our Ethics
                    </a>
                </div>
              </div>

              {/* RIGHT */}
              {/* team cards */}
              <div
                id="team"
                className={`
                  flex flex-col items-center gap-6 sm:gap-8

                  ${heroVisible
                    ? 'opacity-100 scale-100 translate-y-0'
                    : 'opacity-0 scale-95 translate-y-6'}

                  transition-all duration-400 ease-[cubic-bezier(0.22,1,0.36,1)]
                `}
              >

                <div className="sm:hidden flex items-center gap-2 px-3 py-1.5 rounded-full
                  bg-blue-500/10 text-blue-600 text-xs font-semibold tracking-widest uppercase 
                    border border-blue-400/20 mb-10"   style={{ 
                      background: 'rgba(187, 212, 253, 0.1)', 
                      color: '#2563eb', 
                      border: '1px solid rgba(59,130,246,0.2)' 
                    }}>

                  <Binoculars size={14} className="text-blue-500" />
                  Our Research Team
                </div>
                  
                <div
                  className="relative h-96 md:h-[420px] flex items-center justify-center"
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                    {teamMembers.map((member, index) => {
                      const isActive = index === activeIndex;
                      const position = getCardPosition(index);
                      const isCenter = position === "center";
                      const isLeft = position === "left";
                      const isRight = position === "right";

                      return (
                        <div
                          key={member.id}
                          onClick={() => handleCardClick(index)}
                          className={`absolute cursor-pointer transition-all duration-500 ease-out
                            ${isCenter 
                              ? "z-30 scale-100 opacity-100" 
                              : "z-20 scale-90 opacity-80 hover:scale-95 hover:opacity-90"
                            }
                            ${isLeft ? "-translate-x-32 md:-translate-x-44 -rotate-6" : ""}
                            ${isRight ? "translate-x-32 md:translate-x-44 rotate-6" : ""}
                            ${isCenter ? "translate-x-0 rotate-0" : ""}
                            ${position === "hidden" ? "opacity-0 pointer-events-none" : ""}
                          `}
                        >
                          <div className={`relative bg-white rounded-3xl shadow-xl border transition-all duration-500
                            ${isCenter 
                              ? "w-72 md:w-80 p-5 border-blue-200 shadow-blue-100" 
                              : "w-44 md:w-56 p-3 border-slate-200"
                            }`}>

                            {/* image */}
                            <div className="rounded-2xl mb-4 overflow-hidden bg-slate-100 ">
                              {member.image ? (
                                <img
                                  src={member.image}
                                  alt={member.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${member.gradient}`}>
                                  <span className="text-white font-bold text-lg">
                                    {member.initials}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* info */}
                            <div className={`${isCenter ? "space-y-1.5" : "space-y-0.1"}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center flex-wrap">
                                <p
                                  className={`font-semibold ${
                                    isCenter
                                      ? "text-slate-900 text-sm"
                                      : "text-slate-800 text-sm whitespace-nowrap overflow-hidden text-ellipsis"
                                  }`}
                                >
                                  {isCenter ? member.name : getShortName(member.name)}
                                </p>

                                {isCenter && member.suffix && (
                                  <span className="font-semibold text-slate-900 text-sm">
                                  , {member.suffix}
                                  </span>
                                )}
                              </div>

                            {!isCenter && (
                              <BrainCircuit
                                size={13}
                                className="ml-2 text-slate-400 transition-all duration-300"
                              />
                            )}
                            </div>

                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {(isCenter ? member.roles : [member.roles[0]]).map((role, idx) => (
                                  <span
                                    key={idx}
                                    className={`
                                      px-2 py-0.5 rounded-full border font-medium
                                      transition-all duration-300
                                      ${isCenter ? "text-[11px]" : "text-[10px]"}
                                      ${roleColors[idx % roleColors.length]}
                                    `}
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>

                              {/* desc */}
                              <div className={`overflow-hidden transition-all duration-500
                                ${isCenter ? "max-h-20 opacity-100 mt-2" : "max-h-6 opacity-80 mt-1"}
                              `}>
                                <p className={`leading-relaxed ${
                                  isCenter ? "text-[11px] text-slate-400" : "text-[10px] text-slate-400 line-clamp-1"
                                }`}>
                                  {member.description}
                                </p>
                              </div>

                              {/* soc icons*/}
                              <div
                                className={`flex items-center justify-center gap-3 pt-4 mt-0 border-t border-slate-100 transition-all duration-500
                                ${isCenter ? "opacity-100" : "max-h-0 opacity-0"}
                              `}
                              >

                                {/* linkedin */}
                                {member.linkedin && (
                                  <a 
                                    key={`linkedin-${triggerKey}`}
                                    href={member.linkedin}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`
                                      transition-all duration-300 ease-out
                                      ${activeSocial === 'linkedin' && isCenter && highlightActive
                                        ? 'scale-110 ring-1 ring-blue-400 rounded-lg p-1 bg-blue-50 shadow-md'
                                        : 'hover:scale-110'
                                      }
                                    `}
                                  >
                                    <img src="/assets/images/social_logo/linkedin.png" className="w-6 h-6" />
                                  </a>
                                )}

                                <div className="w-px h-5 bg-slate-200" />

                                {/* email */}
                                {member.email && (
                                  <a 
                                    key={`email-${triggerKey}`}
                                    href={member.email}
                                    className={`
                                      transition-all duration-300 ease-out
                                      ${activeSocial === 'email' && isCenter && highlightActive
                                        ? 'scale-110 ring-1 ring-blue-400 rounded-lg p-1 bg-blue-50 shadow-md'
                                        : 'hover:scale-110'
                                      }
                                    `}
                                  >
                                    <img src="/assets/images/social_logo/gmail.png" className="w-6 h-6" />
                                  </a>
                                )}

                                <div className="w-px h-5 bg-slate-200" />

                                {/* github */}
                                {member.github && (
                                  <a 
                                    key={`github-${triggerKey}`}
                                    href={member.github}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={`
                                      transition-all duration-300 ease-out
                                      ${activeSocial === 'github' && isCenter && highlightActive
                                        ? 'scale-110 ring-1 ring-blue-400 rounded-lg p-1 bg-blue-50 shadow-md'
                                        : 'hover:scale-110'
                                      }
                                    `}
                                  >
                                    <img src="/assets/images/social_logo/github.png" className="w-6 h-6" />
                                  </a>
                                )}

                              </div>

                            </div>

                          {/* watermark icon*/}
                          {!isCenter && (
                            <BrainCircuit
                              size={36}
                              className="absolute bottom-3 right-3 text-slate-200 opacity-40"
                            />
                          )}
                                                          
                          </div>
                        </div>
                      );
                    })}
                </div>

                {/* dots*/}
                <div className="flex justify-center gap-2 mt-10">
                    {teamMembers.map((member, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveIndex(index)}
                        aria-label={`View ${member.name}`}
                        className="rounded-full transition-all duration-300"
                        style={{
                          width: index === activeIndex ? '24px' : '8px',
                          height: '8px',
                          background: index === activeIndex ? '#3b82f6' : '#bfdbfe',
                        }}
                      />
                    ))}
                </div>
              
              </div>
            </div>
          </div>
        </section>

        {/* mission section */}
        <section ref={missionRef} className="py-28 relative overflow-hidden bg-white">

          {/* bg*/}
          <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl opacity-60" />

          {/* dot grid */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.25]"
            style={{
              backgroundImage: 'radial-gradient(circle, #75b7fa 1px, transparent 1px)',
              backgroundSize: '36px 36px'
            }}
          />

          <div
            className="section-container relative z-10"
          >

            <div className="max-w-6xl">

              {/* badge */}
              <div className={`${base} ${missionVisible ? show : hidden} inline-flex items-center gap-2 px-3 py-1.5 rounded-full 
                bg-blue-500/10 text-blue-600 text-xs font-bold tracking-widest uppercase 
                border border-blue-400/20 mb-6`}>

                <Goal className="w-3.5 h-3.5 text-blue-500" />
                Our Mission
              </div>

              {/* headline */}
              <h2 className="leading-tight mb-8">

                <span className={`${base} delay-100 ${missionVisible ? show : hidden} block text-3xl md:text-4xl lg:text-5xl font-extrabold`}>
                  <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                    Early Awareness Saves Lives.
                  </span>
                </span>

                <span className={`${base} delay-200 ${missionVisible ? show : hidden} block text-2xl md:text-3xl lg:text-4xl font-semibold text-slate-600 mt-3`}>
                  We create AI-driven solutions that promote early awareness, helping people act before conditions progress.
                </span>

              </h2>

              {/* desc*/}
              <div className={`${base} delay-300 ${missionVisible ? show : hidden} mt-10`}>
                <div className="relative bg-slate-50/70 border border-slate-100 rounded-xl p-6 pl-8">

                  {/* left strip */}
                  <div className="absolute left-0 top-0 h-full w-1.5 
                    bg-gradient-to-b from-blue-500 to-cyan-400 
                    rounded-l-xl"
                  />

                  {/* text */}
                  <p className="text-base md:text-lg text-slate-500 leading-relaxed">
                    At Insignta AI, we believe that early awareness is the key to better health outcomes. 
                    By leveraging deep learning and tongue image analysis, we’re creating accessible AI tools that help individuals recognize potential 
                    risks early—empowering timely action and informed decisions.                  
                  </p>

                </div>
              </div>

            </div>

          </div>
        </section>

        {/* ethics section */}
        <section
          ref={ethicsRef}
          id="ethics"
          className="py-20 lg:py-28 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden"
        >
          {/* bg */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)`,
              backgroundSize: '32px 32px',
            }}
          />
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />

          <div className="section-container relative z-10">
            
            {/* header */}
            <div className="mb-16 grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
              
              {/* left */}
              <div>
                <div className={`${base} ${ethicsVisible ? show : hidden} inline-flex items-center gap-2 bg-white/10 border border-white/20 text-blue-300 rounded-full px-4 py-1.5 text-xs font-semibold mb-4 uppercase tracking-wider`}>
                  <Blocks size={14} className='text-blue-300' />
                  Designed Responsibly
                </div>

                <h2 className={`${base} delay-100 ${ethicsVisible ? show : hidden} text-3xl md:text-4xl font-bold text-white leading-tight`}>
                  Our Ethical{' '}
                  <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                    Commitments
                  </span>
                </h2>
              </div>

              {/* right */}
              <p className={`${base} delay-200 ${ethicsVisible ? show : hidden} text-slate-400 text-base leading-relaxed lg:text-right max-w-md lg:ml-auto`}>
                Insignta AI is designed to support early awareness—not replace professional medical diagnosis. 
                These principles ensure our technology remains safe, transparent, and centered on people.              
              </p>
            </div>
            
            {/* cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              {ethicsItems.map((item, i) => {
                const Icon = item.icon;

                return (
                <div
                  key={i}
                  className={`
                    h-full
                    ${ethicsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                    transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]
                  `}
                  style={{
                    transitionDelay: `${300 + i * 120}ms`,
                  }}
                >
                  <div className="h-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02]">
                    <div className={`w-12 h-12 ${item.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon size={20} className={item.iconColor} />
                    </div>

                    <span className={`text-xs font-semibold px-3 py-1 rounded-full ${item.badgeColor}`}>
                      {item.badge}
                    </span>

                    <h3 className="text-white font-semibold mt-3 mb-1">
                      {item.title}
                    </h3>

                    <p className="text-slate-400 text-sm">
                      {item.description}
                    </p>
                  </div>
                </div>
                );
              })}
            </div>


          </div>
        </section>

        <CTASection sectionBg="bg-white" />
      </main>

      <Footer />
    </div>
  );
}

export default function AboutPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <AboutPageContent />
    </Suspense>
  );
}
